import React from 'react';
import {
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
  Avatar,
  Box,
  Typography,
  Container,
  Toolbar,
  Chip,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import LoginSharpIcon from '@mui/icons-material/LoginSharp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import useAppBar from '../../Hooks/Dashboard/useAppBar';
import { AppBarProps } from '../../Objects/interfaces/NavBarInterface';
import ButtonField from '../Ui/ButtonField';
import ElevatorAppBar from './ElevatorAppBar';
import NotificationBell from './NotificationBell';
import HelpButton from './HelpButton';
import { DASHBOARD_NAV_INDEXES } from './AppBarConfigs';

// Pages du tableau de bord unifié (entrées du menu et Paramètres, voir DASHBOARD_NAV_INDEXES).
// Les autres index (4 parcours d'inscription, 5 à 7 AppBar minimale, 8 pages annexes) n'ont pas de cloche.
const NOTIFICATION_BELL_INDEXES = DASHBOARD_NAV_INDEXES;

const AppBar: React.FC<AppBarProps> = ({ theme, index, stepper, isDeclarons }) => {

  const {
    selectedIndex,
    scrolling,
    anchorEl,
    anchorElNav,
    user,
    brandConfig,
    classes,
    appConfig,
    demoSession,
    accounts,
    isFullyValidatedTeachr,
    isClient,
    handleNavigation,
    handleChangeRoute,
    handleAccountMenuOpen,
    handleAccountMenuClose,
    handleOpenNavMenu,
    handleCloseNavMenu,
    location,
    displayLogo
  } = useAppBar({ index: index, theme: theme, isDeclarons: isDeclarons });

  const isDark = theme === "dark" ? (isDeclarons ? false : true) : false;

  // Cloche des notifications : prof et client connectés, uniquement dans le tableau de bord
  const showNotifications = Boolean(appConfig.showProfile && user && index !== undefined && NOTIFICATION_BELL_INDEXES.includes(index));

  const cardBg = isDark ? scrolling ? '/rgba(15, 23, 42, 0.96)' : '/rgba(15, 23, 42, 0.55)' : 'rgba(255, 255, 255, 0.97)';

  const cardBorder = isDark
    ? '1px solid rgba(255,255,255,0.08)'
    : '1px solid rgba(0,0,0,0.06)';

  // Rendu du menu desktop
  const renderDesktopMenu = () => (
    <Container maxWidth="xl" className='lg:block hidden w-full'>
      <Toolbar disableGutters sx={{ py: index === 4 ? 3 : 1, position: 'relative' }}>
        {/* Logo - gauche */}
        <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center', cursor: 'pointer', zIndex: 1 }}>
          {displayLogo()}
        </Box>

        {/* Nav items : à la suite du logo, texte seul, l'onglet courant dans la couleur de la marque (maquettes « Déclarer un cours ») */}
        {index !== 5 && (
          <Box sx={{
            ml: 5,
            display: { xs: 'none', md: 'flex' },
            alignItems: 'center',
            gap: 3.5,
          }}>
            {appConfig.navigationItems.map((item, navIdx) => (
              <Box
                key={item.id}
                component="button"
                onClick={() => handleNavigation(item.path, navIdx, item.url)}
                aria-current={selectedIndex === navIdx ? 'page' : undefined}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 0.5,
                  px: 0,
                  py: 0.75,
                  border: 'none',
                  background: 'transparent',
                  cursor: 'pointer',
                  fontFamily: brandConfig.fontFamily,
                  fontSize: '0.875rem',
                  fontWeight: 700,
                  color: selectedIndex === navIdx
                    ? brandConfig.colors.primary
                    : isDark ? 'rgba(255,255,255,0.82)' : brandConfig.colors.text,
                  transition: 'color 0.18s ease',
                  '&:hover': {
                    color: isDark ? '#fff' : brandConfig.colors.primary,
                  },
                }}
              >
                {item.icon && <Box sx={{ display: 'flex', fontSize: '1rem' }}>{item.icon}</Box>}
                <span>{item.label as string}</span>
              </Box>
            ))}
          </Box>
        )}

        {/* Spacer */}
        <Box sx={{ flexGrow: 1 }} />

        {/* Right actions - droite */}
        <Box sx={{ flexGrow: 0, display: { xs: 'none', md: 'flex' }, alignItems: 'center', gap: 1.5, zIndex: 1 }}>
          {index === 5 && (
            <ButtonField
              label={user ? "Déconnexion" : (location.pathname === "/" ? "S'inscrire" : "Se connecter")}
              variant="contained"
              className={`font-bold px-5 ${isDeclarons ? `${classes.primaryBg} text-white` : `${classes.primaryBg} text-white`} shadow-md hover:shadow-lg transition-all duration-200`}
              startIcon={user ? <LogoutOutlinedIcon className="w-4 h-4" /> : <LoginSharpIcon className="w-4 h-4" />}
              onClick={handleChangeRoute}
            />
          )}

          {/* aide et contact : même visibilité que la cloche, sur toutes les pages du tableau de bord */}
          {showNotifications && <HelpButton isDark={isDark} variant="pill" />}
          {showNotifications && <NotificationBell isDark={isDark} />}

          {appConfig.showProfile && user && index !== 5 && (
            <Tooltip title="Menu compte" arrow>
              <Chip
                onClick={handleAccountMenuOpen}
                avatar={
                  <Avatar
                    alt={demoSession.user.name}
                    src={demoSession.user.image}
                    sx={{ width: 28, height: 28 }}
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <span>{demoSession.user.name}</span>
                    <KeyboardArrowDownIcon sx={{ fontSize: 14 }} />
                  </Box>
                }
                size="medium"
                sx={{
                  backgroundColor: brandConfig.colors.primary,
                  color: 'white',
                  fontWeight: 700,
                  fontFamily: brandConfig.fontFamily,
                  fontSize: '0.8rem',
                  height: 40,
                  px: 0.5,
                  cursor: 'pointer',
                  boxShadow: `0 2px 12px ${brandConfig.colors.primary}44`,
                  transition: 'all 0.2s ease',
                  '&:hover': {
                    boxShadow: `0 4px 20px ${brandConfig.colors.primary}55`,
                    transform: 'translateY(-1px)',
                    filter: 'brightness(1.06)',
                  },
                  '& .MuiChip-avatar': { width: 28, height: 28 },
                }}
              />
            </Tooltip>
          )}

          {appConfig.showAuthButton && !user && index !== 5 && (
            <ButtonField
              label={location.pathname === "/" ? "S'inscrire" : "Se connecter"}
              variant="contained"
              className={`font-bold px-5 ${classes.primaryBg} text-white shadow-md hover:shadow-lg transition-all duration-200`}
              startIcon={<LoginSharpIcon className="w-4 h-4" />}
              onClick={handleChangeRoute}
            />
          )}
        </Box>
      </Toolbar>
    </Container>
  );

  // Rendu du menu mobile
  const renderMobileMenu = () => (
    <Container maxWidth="xl" className='lg:hidden w-full'>
      <Toolbar disableGutters sx={{ minHeight: '60px !important' }}>
        {/* Hamburger */}
        <Box sx={{ flexGrow: 0, display: 'flex', alignItems: 'center' }}>
          <IconButton
            size="medium"
            aria-label="menu navigation"
            aria-controls="menu-appbar"
            aria-haspopup="true"
            onClick={handleOpenNavMenu}
            className={`${index === 5 ? 'hidden' : ''}`}
            sx={{
              color: isDark ? 'rgba(255,255,255,0.85)' : '#374151',
              borderRadius: '10px',
              '&:hover': { backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
            }}
          >
            <MenuIcon />
          </IconButton>
          <Menu
            id="menu-appbar"
            anchorEl={anchorElNav}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
            keepMounted
            transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            open={Boolean(anchorElNav)}
            onClose={handleCloseNavMenu}
            sx={{
              '& .MuiPaper-root': {
                borderRadius: '16px',
                boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
                mt: 1.5,
                minWidth: 220,
                border: '1px solid rgba(0,0,0,0.05)',
                overflow: 'hidden',
              },
            }}
          >
            {(isFullyValidatedTeachr || isClient) && appConfig.navigationItems.map((item, navIdx) => (
              <MenuItem
                key={item.id}
                onClick={() => { handleNavigation(item.path, navIdx, item.url); handleCloseNavMenu(); }}
                sx={{
                  fontFamily: brandConfig.fontFamily,
                  color: selectedIndex === navIdx ? brandConfig.colors.primary : '#374151',
                  fontWeight: selectedIndex === navIdx ? 700 : 500,
                  mx: 1,
                  my: 0.25,
                  borderRadius: '10px',
                  '&:hover': { backgroundColor: `${brandConfig.colors.primary}10` },
                }}
              >
                {item.icon && <Box sx={{ mr: 1.5, display: 'flex', opacity: 0.75 }}>{item.icon}</Box>}
                <Typography sx={{ fontFamily: brandConfig.fontFamily, fontSize: '0.9rem' }}>
                  {item.label}
                </Typography>
              </MenuItem>
            ))}
          </Menu>
        </Box>

        {/* Logo centré */}
        <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center' }}>
          {displayLogo()}
        </Box>

        {/* Actions Mobile */}
        <Box sx={{ flexGrow: 0, display: 'flex', gap: 1, alignItems: 'center' }}>
          {showNotifications && <HelpButton isDark={isDark} size="small" />}
          {showNotifications && <NotificationBell isDark={isDark} size="small" />}
          {appConfig.showProfile && user && (
            <Tooltip title="Menu compte" arrow>
              <IconButton
                onClick={handleAccountMenuOpen}
                size="small"
                sx={{ p: 0.25 }}
              >
                <Avatar
                  src={demoSession.user.image}
                  sx={{
                    width: 34, height: 34,
                    border: `2px solid ${brandConfig.colors.primary}`,
                    boxShadow: `0 2px 8px ${brandConfig.colors.primary}33`,
                  }}
                />
              </IconButton>
            </Tooltip>
          )}
          {appConfig.showAuthButton && !user && index !== 5 && (
            <ButtonField
              label={location.pathname === "/" ? "S'inscrire" : "Se connecter"}
              variant="contained"
              className={`font-bold ${classes.primaryBg} text-white text-xs px-3`}
              startIcon={<LoginSharpIcon sx={{ fontSize: '1rem !important' }} />}
              onClick={handleChangeRoute}
            />
          )}
        </Box>
      </Toolbar>
    </Container>
  );

  return (
    <>
      <ElevatorAppBar
        index={index}
        theme={theme}
        brandConfig={brandConfig}
        anchorEl={anchorEl}
        handleAccountMenuClose={handleAccountMenuClose}
        accounts={accounts}
        demoSession={demoSession}
        scrolling={scrolling}
        className="bg-transparent lg:px-8 md:px-4 px-3 pt-4 pb-0"
      >
        <Box
          sx={{
            backgroundColor: cardBg,
            backdropFilter: 'blur(20px)',
            WebkitBackdropFilter: 'blur(20px)',
            borderRadius: '10px',
            border: cardBorder,
            boxShadow: scrolling
              ? '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)'
              : '0 4px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)',
            transition: 'box-shadow 0.3s ease, background-color 0.3s ease',
            overflow: 'hidden',
          }}
        >
          {renderDesktopMenu()}
          {renderMobileMenu()}
        </Box>
        {stepper}
      </ElevatorAppBar>
    </>
  );
};

export default AppBar;
